import React, { useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../../utils/fileUtils';
import './BoardForm.css';

const BoardForm: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [lnks, setLnks] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // 파일 드래그 앤 드롭 처리
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  // 파일 선택 처리
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...selectedFiles]);
      e.target.value = ''; // input 초기화
    }
  };

  // 개별 파일 삭제
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await axiosInstance.post('/api/boards', { category, title, content, lnks });
      const boardId = res.data.id;

      // 파일 업로드
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('board_id', boardId);
        formData.append('description', '');
        await axiosInstance.post('/api/boards/attach', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      navigate(`/boards/${category}`);
    } catch (err) {
      setMessage('오류 발생!');
    }
  };

  return (
    <div className="board-form-wrapper">
      <h2>게시글 등록</h2>

      <form onSubmit={handleSubmit}>
        {/* 제목 */}
        <div className="board-form-group">
          <label className="board-form-label">제목 *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="board-form-input"
            required
          />
        </div>

        {/* 내용 */}
        <div className="board-form-group">
          <label className="board-form-label">내용 *</label>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            className="board-form-textarea"
            required
          />
        </div>

        {/* 관련 링크 */}
        <div className="board-form-group">
          <label className="board-form-label">관련링크 (여러 개인 경우 ;로 구분)</label>
          <input
            type="text"
            value={lnks}
            onChange={e => setLnks(e.target.value)}
            placeholder="https://example.com;https://example2.com"
            className="board-form-input"
          />
        </div>

        {/* 첨부파일 */}
        <div className="board-form-group">
          <label className="board-form-label">첨부파일</label>

          {/* 드래그 앤 드롭 영역 */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`board-file-dropzone ${isDragOver ? 'drag-over' : ''}`}
          >
            <div className="board-file-dropzone-hint">
              📁 파일을 드래그 앤 드롭하거나 클릭하여 선택하세요
            </div>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="fileInput"
            />
            <button
              type="button"
              onClick={() => document.getElementById('fileInput')?.click()}
              className="board-file-select-btn"
            >
              파일 선택
            </button>
          </div>

          {/* 선택된 파일 목록 */}
          {files.length > 0 && (
            <div className="board-file-list">
              <div className="board-file-list-title">
                선택된 파일 ({files.length}개)
              </div>
              {files.map((file, index) => {
                const fileInfo = getFileTypeInfo(file.name, file.type);
                return (
                  <div key={index} className="board-file-item">
                    <div className="board-file-item-info">
                      <span className="board-file-icon">{fileInfo.icon}</span>
                      <div>
                        <div className="board-file-name" style={{ color: fileInfo.color }}>
                          {truncateFileName(file.name, 30)}
                        </div>
                        <div className="board-file-meta">
                          {fileInfo.label} • {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="board-file-remove-btn"
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="board-form-actions">
          <button
            type="button"
            onClick={() => navigate(`/boards/${category}`)}
            className="board-form-btn-cancel"
          >
            취소
          </button>
          <button type="submit" className="board-form-btn-submit">
            등록하기
          </button>
        </div>

        {message && (
          <div className="board-form-error">{message}</div>
        )}
      </form>
    </div>
  );
};

export default BoardForm;